import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Checkbox, EmptyState, Field, Modal, Notice, PageHeader, RowActions, Table, Tag } from '@voice/ui';
import { errorText, formatDateTime } from '../components/common';
import { api } from '../lib/api';
import styles from './Pages.module.css';

interface Key {
  id: string;
  name: string;
  prefix: string;
  createdAt: string;
  lastUsedAt: string | null;
}
interface Hook {
  id: string;
  name: string;
  url: string;
  events: string[];
  enabled: boolean;
  lastStatus: string | null;
  lastAt: string | null;
}
interface Response {
  keys: Key[];
  webhooks: Hook[];
  events: string[];
}

const EVENT_LABEL: Record<string, string> = { 'call.ended': 'Anruf beendet', 'voicemail.new': 'Neue Sprachnachricht', 'message.new': 'Neue Nachricht aus dem Sekretariat' };
const KEY = ['integrations'] as const;

export function Integrations() {
  const query = useQuery({ queryKey: KEY, queryFn: () => api<Response>('/integrations') });
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: KEY });
  const [secret, setSecret] = useState<{ title: string; label: string; value: string } | null>(null);
  const [hookForm, setHookForm] = useState(false);
  const [keyName, setKeyName] = useState('');
  const addKey = useMutation({
    mutationFn: () => api<{ key: string }>('/integrations/keys', { method: 'POST', body: { name: keyName } }),
    onSuccess: (r) => {
      setKeyName('');
      setSecret({ title: 'Neuer API-Schlüssel', label: 'Schlüssel', value: r.key });
      void refresh();
    },
  });
  const delKey = useMutation({ mutationFn: (id: string) => api(`/integrations/keys/${id}`, { method: 'DELETE' }), onSuccess: refresh });
  const toggle = useMutation({ mutationFn: (h: Hook) => api(`/integrations/webhooks/${h.id}`, { method: 'PATCH', body: { enabled: !h.enabled } }), onSuccess: refresh });
  const delHook = useMutation({ mutationFn: (id: string) => api(`/integrations/webhooks/${id}`, { method: 'DELETE' }), onSuccess: refresh });
  const test = useMutation({ mutationFn: (id: string) => api(`/integrations/webhooks/${id}/test`, { method: 'POST' }), onSuccess: refresh });
  const data = query.data;

  return (
    <div className={styles.page}>
      <PageHeader title="Schnittstellen" />

      <section>
        <h2 className={styles.heading}>API-Schlüssel</h2>
        <p className={styles.note} style={{ marginBottom: 12 }}>
          Mit einem Schlüssel können andere Programme (CRM, Skripte) Anrufe auslösen und den Zustand abfragen. Der Schlüssel wird nur einmal angezeigt.
        </p>
        {data?.keys.length === 0 && <EmptyState>Noch kein Schlüssel.</EmptyState>}
        {data && data.keys.length > 0 && (
          <Table aria-label="API-Schlüssel">
            <thead>
              <tr>
                <th>Name</th>
                <th>Beginnt mit</th>
                <th>Zuletzt benutzt</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.keys.map((k) => (
                <tr key={k.id}>
                  <th scope="row">{k.name}</th>
                  <td className={styles.mono}>{k.prefix}…</td>
                  <td>{k.lastUsedAt ? formatDateTime(k.lastUsedAt) : <span className={styles.muted}>nie</span>}</td>
                  <td>
                    <RowActions>
                      <Button variant="secondary" onClick={() => confirm(`Schlüssel „${k.name}“ löschen? Programme damit verlieren sofort den Zugriff.`) && delKey.mutate(k.id)}>
                        Löschen
                      </Button>
                    </RowActions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        <form
          className={styles.inline}
          style={{ marginTop: 16, alignItems: 'flex-end' }}
          onSubmit={(e) => {
            e.preventDefault();
            addKey.mutate();
          }}
        >
          <Field label="Neuer Schlüssel für" value={keyName} onChange={(e) => setKeyName(e.target.value)} placeholder="z. B. CRM" style={{ flex: '1 1 240px' }} />
          <Button type="submit" variant="secondary" disabled={!keyName || addKey.isPending}>
            Erzeugen
          </Button>
        </form>
        {addKey.isError && <Notice tone="error">{errorText(addKey.error)}</Notice>}
        <details style={{ marginTop: 16 }}>
          <summary>So wird er benutzt</summary>
          <pre className={styles.code}>{`# Anruf auslösen: Nebenstelle 101 klingelt, nach dem Abheben wird gewählt
curl -X POST ${location.origin}/api/v1/dial \\
  -H "Authorization: Bearer vk_…" -H "Content-Type: application/json" \\
  -d '{"extension":"101","number":"+49371123456"}'

# Wer ist erreichbar?     GET ${location.origin}/api/v1/presence
# Laufende Gespräche:     GET ${location.origin}/api/v1/calls`}</pre>
        </details>
      </section>

      <section className={styles.section}>
        <div className={styles.inline} style={{ justifyContent: 'space-between' }}>
          <h2 className={styles.heading}>Webhooks</h2>
          <Button variant="secondary" onClick={() => setHookForm(true)}>
            Webhook anlegen
          </Button>
        </div>
        <p className={styles.note} style={{ marginBottom: 12 }}>
          Die Anlage ruft eine Adresse auf, sobald etwas passiert (JSON, signiert im Header <span className={styles.mono}>x-voice-signature</span> mit HMAC-SHA256).
        </p>
        {data?.webhooks.length === 0 && <EmptyState>Noch kein Webhook.</EmptyState>}
        {data && data.webhooks.length > 0 && (
          <Table aria-label="Webhooks">
            <thead>
              <tr>
                <th>Name</th>
                <th>Ereignisse</th>
                <th>Letzte Zustellung</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.webhooks.map((h) => (
                <tr key={h.id}>
                  <th scope="row">
                    {h.name} {!h.enabled && <Tag tone="warn">aus</Tag>}
                    <div className={styles.muted}>{h.url}</div>
                  </th>
                  <td>{h.events.map((e) => EVENT_LABEL[e] ?? e).join(', ')}</td>
                  <td>{h.lastAt ? `${formatDateTime(h.lastAt)} – ${h.lastStatus}` : <span className={styles.muted}>noch nie</span>}</td>
                  <td>
                    <RowActions>
                      <Button variant="secondary" onClick={() => test.mutate(h.id)}>
                        Testen
                      </Button>
                      <Button variant="secondary" onClick={() => toggle.mutate(h)}>
                        {h.enabled ? 'Ausschalten' : 'Einschalten'}
                      </Button>
                      <Button variant="secondary" onClick={() => confirm('Webhook löschen?') && delHook.mutate(h.id)}>
                        Löschen
                      </Button>
                    </RowActions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </section>

      {hookForm && data && <HookForm events={data.events} onClose={() => setHookForm(false)} onCreated={(s) => { setHookForm(false); setSecret({ title: 'Webhook angelegt', label: 'Geheimer Schlüssel zum Prüfen der Signatur', value: s }); void refresh(); }} />}
      {secret && (
        <Modal open onClose={() => setSecret(null)} title={secret.title} description="Dieser Wert wird nur jetzt angezeigt – bitte kopieren und sicher ablegen." size="md" footer={<Button onClick={() => setSecret(null)}>Fertig</Button>}>
          <dl className={styles.credentials}>
            <dt>{secret.label}</dt>
            <dd>
              {secret.value}
              <button type="button" className={styles.copy} onClick={() => void navigator.clipboard?.writeText(secret.value)}>
                Kopieren
              </button>
            </dd>
          </dl>
        </Modal>
      )}
    </div>
  );
}

function HookForm({ events, onClose, onCreated }: { events: string[]; onClose: () => void; onCreated: (secret: string) => void }) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('https://');
  const [chosen, setChosen] = useState<string[]>(['call.ended']);
  const create = useMutation({
    mutationFn: () => api<{ secret: string }>('/integrations/webhooks', { method: 'POST', body: { name, url, events: chosen } }),
    onSuccess: (r) => onCreated(r.secret),
  });
  return (
    <Modal
      open
      onClose={onClose}
      title="Webhook anlegen"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={() => create.mutate()} disabled={!name || !url || chosen.length === 0 || create.isPending}>
            Anlegen
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        <Field label="Adresse" value={url} onChange={(e) => setUrl(e.target.value)} hint="Muss aus dem Internet erreichbar sein (https)." />
        <fieldset className={styles.fieldset}>
          <legend className={styles.labelText}>Bei diesen Ereignissen</legend>
          <div className={styles.stack}>
            {events.map((e) => (
              <Checkbox key={e} label={EVENT_LABEL[e] ?? e} checked={chosen.includes(e)} onChange={(ev) => setChosen((c) => (ev.target.checked ? [...c, e] : c.filter((x) => x !== e)))} />
            ))}
          </div>
        </fieldset>
        {create.isError && <Notice tone="error">{errorText(create.error)}</Notice>}
      </div>
    </Modal>
  );
}
